import Typography from '@material-ui/core/Typography';
import { FC, ReactElement } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { ReactComponent as DeleteIcon } from '~/ui/assets/images/delete.svg';
import ButtonIcon from '~/ui/components/common/ButtonIcon';

import AllocationClientRow from '../../../shared/components/AllocationClientRow/AllocationClientRow';
import MemoField from '../../../shared/components/MemoField/MemoField';
import useAllocationChangeTracking from '../../../shared/hooks/useAllocationChangeTracking';
import { IEditAllocationFormValues } from '../../../shared/model/types';

import layoutStyles from '../../../shared/components/EditAllocationFormLayout/EditAllocationFormLayout.module.scss';
import variables from '~/ui/assets/styles/colors.module.scss';
import styles from './CrisisAllocationClientsList.module.scss';

interface IProps {
  teamMemberIdx: number;
}

const CrisisAllocationClientsList: FC<IProps> = ({ teamMemberIdx }): ReactElement => {
  const crisisAllocationsFieldName =
    `teamMembersAllocations.${teamMemberIdx}.crisisAllocations` as const;
  const { control } = useFormContext<IEditAllocationFormValues>();
  const { markChanged, markDeleted } = useAllocationChangeTracking<IEditAllocationFormValues>({
    changedField: 'changedCrisisAllocationsIds',
    deletedField: 'deletedCrisisAllocationsIds',
  });

  const { fields: crisisAllocationsFields, remove: removeCrisisAllocations } = useFieldArray({
    control,
    name: crisisAllocationsFieldName,
  });

  if (!crisisAllocationsFields.length) {
    return null;
  }

  return (
    <div className={styles.crisis}>
      <Typography variant="subtitle1" className={layoutStyles.assignedClientsSectionTitle}>
        Crisis Allocations
      </Typography>
      {crisisAllocationsFields.map((field, clientIdx) => {
        const isDisabled = field.allocation?.resolved;
        const memoFieldName = `teamMembersAllocations.${teamMemberIdx}.crisisAllocations.${clientIdx}.memo`;

        const client = {
          photo: field.photo,
          name: field.name,
          isResolved: field.allocation?.resolved,
          isCrisis: field.allocation?.isCrisis,
          clientId: field.clientId,
          teamId: field.teamId,
        };

        const handleRemoveClient = () => {
          removeCrisisAllocations(clientIdx);
          markDeleted(field.allocation.id);
        };

        return (
          <AllocationClientRow
            key={field.id}
            client={client}
            memoField={
              <MemoField
                disabled={isDisabled}
                memoFieldName={memoFieldName}
                onMemoChange={() => markChanged(field.allocation.id)}
              />
            }
            actions={
              <ButtonIcon disabled={isDisabled} onClick={handleRemoveClient}>
                <DeleteIcon width={20} color={variables.colorRed} />
              </ButtonIcon>
            }
          />
        );
      })}
    </div>
  );
};

export default CrisisAllocationClientsList;
